<template>
  <div class="">
    <div class="d-sm-flex align-items-center justify-content-between mb-2">
      <div>
        <ol class="breadcrumb fs-sm mb-1">
          <li class="breadcrumb-item">
            <router-link to="/dashboard">Dashboard</router-link>
          </li>
          <li class="breadcrumb-item active" aria-current="page">Trips</li>
        </ol>
        <h4 class="main-title mb-0">Trips</h4>
      </div>
      <div class="d-flex align-items-center">
        <button
          type="button"
          class="btn btn-outline-primary me-2"
          @click="openFilterModal"
        >
          <i class="ri-filter-3-line fs-18 lh-1"></i> Filter
          <span
            v-if="hasActiveFilters"
            class="badge bg-primary ms-1"
          >{{ activeFilterCount }}</span>
        </button>
        <router-link
          to="/trip/create"
          v-can="'trips.create'"
          type="submit"
          class="btn btn-primary"
          ><i class="ri-list-check fs-18 lh-1"></i> Add Trip</router-link
        >
      </div>
    </div>
    <div class="row">
      <div class="col-12">
        <div class="card card-one">
          <div class="card-header d-flex justify-content-between">
            <h6 class="card-title">Trips</h6>
            <div>
              <span
                v-if="$store.getters.download_status || bgExporting"
                class="me-2"
              >
                <span class="text-muted me-1">Download Status:</span>
                <span class="badge" :class="statusBadgeClass">
                  {{ statusLabel }}
                  <span
                    v-if="isStatusInProgress"
                    class="ellipsis"
                    aria-hidden="true"
                  ></span>
                </span>
              </span>
              <button
                class="btn btn-primary btn-sm me-2"
                @click="startBackgroundExportSelected"
                :disabled="bgExporting || selectedTripIds.length === 0"
              >
                <i class="ri-checkbox-multiple-line"></i>
                <span v-if="!bgExporting">Download Selected (ZIP)</span>
                <span v-else>Queued...</span>
              </button>
              <span class="text-muted" v-if="selectedTripIds.length > 0"
                >Selected: {{ selectedTripIds.length }}</span
              >
            </div>
          </div>
          <div class="card-body">
            <div class="table-responsive table-responsive-sm">
              <table class="table table-bordered table-striped table-sm">
                <thead>
                  <tr style="background-color: #9b9b9b; color: white">
                    <th class="text-center" width="2%">#</th>
                    <th @click="sort('trip_no')" width="5%">
                      Trip No
                      <span>
                        <i
                          v-if="
                            meta.keyword == 'trip_no' && meta.order_by == 'asc'
                          "
                          class="ri-arrow-up-line"
                        ></i>
                        <i
                          v-else-if="
                            meta.keyword == 'trip_no' && meta.order_by == 'desc'
                          "
                          class="ri-arrow-down-line"
                        ></i>
                        <i v-else class="fas fa-sort"></i>
                      </span>
                    </th>
                    <th
                      class="text-wrap"
                      style="min-width: 6rem; max-width: 8rem"
                      @click="sort('trip_start_date_time')"
                    >
                      Trip Start Date & Time
                      <span>
                        <i
                          v-if="
                            meta.keyword == 'trip_start_date_time' &&
                            meta.order_by == 'asc'
                          "
                          class="ri-arrow-up-line"
                        ></i>
                        <i
                          v-else-if="
                            meta.keyword == 'trip_start_date_time' &&
                            meta.order_by == 'desc'
                          "
                          class="ri-arrow-down-line"
                        ></i>
                        <i v-else class="fas fa-sort"></i>
                      </span>
                    </th>
                    <th
                      class="text-wrap"
                      style="min-width: 6rem; max-width: 8rem"
                      @click="sort('status_date_time')"
                    >
                      Trip End Date & Time
                      <span>
                        <i
                          v-if="
                            meta.keyword == 'status_date_time' &&
                            meta.order_by == 'asc'
                          "
                          class="ri-arrow-up-line"
                        ></i>
                        <i
                          v-else-if="
                            meta.keyword == 'status_date_time' &&
                            meta.order_by == 'desc'
                          "
                          class="ri-arrow-down-line"
                        ></i>
                        <i v-else class="fas fa-sort"></i>
                      </span>
                    </th>
                    <th @click="sort('customer_id')" width="10%">
                      Customer
                      <span>
                        <i
                          v-if="
                            meta.keyword == 'customer_id' &&
                            meta.order_by == 'asc'
                          "
                          class="ri-arrow-up-line"
                        ></i>
                        <i
                          v-else-if="
                            meta.keyword == 'customer_id' &&
                            meta.order_by == 'desc'
                          "
                          class="ri-arrow-down-line"
                        ></i>
                        <i v-else class="fas fa-sort"></i>
                      </span>
                    </th>
                    <th>Grade</th>
                    <th>Lot No</th>
                    <th>Packing</th>
                    <th>Loaded Bags</th>
                    <th>Loaded Quantity</th>
                    <th @click="sort('container_no')" width="5%">
                      Container No
                      <span>
                        <i
                          v-if="
                            meta.keyword == 'container_no' &&
                            meta.order_by == 'asc'
                          "
                          class="ri-arrow-up-line"
                        ></i>
                        <i
                          v-else-if="
                            meta.keyword == 'container_no' &&
                            meta.order_by == 'desc'
                          "
                          class="ri-arrow-down-line"
                        ></i>
                        <i v-else class="fas fa-sort"></i>
                      </span>
                    </th>
                    <th class="text-center" v-can="'trips.delete'" width="5%">
                      Status
                    </th>
                    <th class="text-center" v-can="'trips.update'" width="5%">
                      Actions
                    </th>
                    <th
                      class="text-center"
                      colspan="2"
                      width="5%"
                      v-if="trip_status"
                    >
                      Downloads
                    </th>
                    <th class="text-center" width="4%">
                      Select
                      <div class="mt-1">
                        <input
                          type="checkbox"
                          ref="selectAllCheckbox"
                          :checked="isAllSelectedOnPage"
                          @change="toggleSelectAllOnPage($event)"
                        />
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-if="trips.length == 0">
                    <td colspan="14" class="text-center">No records found</td>
                  </tr>
                  <tr v-for="(trip, key) in trips" :key="key">
                    <td class="text-center">{{ meta.from + key }}</td>
                    <td>{{ trip.trip_no }}</td>
                    <td
                      class="text-wrap"
                      style="min-width: 6rem; max-width: 8rem"
                    >
                      {{ convertDateFormat(trip, trip.trip_start_date_time) }}
                    </td>
                    <td
                      class="text-wrap"
                      style="min-width: 6rem; max-width: 8rem"
                    >
                      {{
                        trip.status === "Completed" || trip.status === "Force Complete"
                          ? convertDateFormat(trip, trip.status_date_time)
                          : ""
                      }}
                    </td>
                    <td>{{ trip.customer?.customer_name }}</td>
                    <td>{{ trip.details?.grade }}</td>
                    <td>{{ trip.details?.lot_nos }}</td>
                    <td>{{ trip.details?.packings }}</td>
                    <td>{{ trip.details?.loading_bags }}</td>
                    <td>{{ trip.details?.loading_quantity }}</td>
                    <!-- <td>{{ trip.transporter?.transporter_name }}</td>
                                        <td>{{ trip.wagon_no }}</td> -->
                    <td>{{ trip.container_no }}</td>
                    <td class="text-center">
                      <span :class="trip.badge">{{ trip.status }}</span>
                    </td>
                    <td class="text-center" v-can="'trips.update'">
                      <nav>
                        <a
                          href="javascript:void(0)"
                          class="text-success me-2"
                          @click.prevent="edit(trip)"
                          v-if="trip.status_flag == 0 || trip.status_flag == 2"
                          ><i class="ri-pencil-line fs-18 lh-1"></i
                        ></a>
                        <a
                          href="javascript:void(0)"
                          class="text-success me-2"
                          @click.prevent="edit(trip)"
                          v-if="
                            trip.status_flag == 1 &&
                            $store.getters.user.role.role_code == 'ADMIN'
                          "
                          ><i class="ri-pencil-line fs-18 lh-1"></i
                        ></a>
                        <a
                          href="javascript:void(0)"
                          class="text-success me-2"
                          @click.prevent="view(trip)"
                          ><i class="ri-eye-line fs-18 lh-1"></i
                        ></a>
                      </nav>
                    </td>
                    <td class="text-center" v-if="trip?.status_flag == 1">
                      <a
                        href="javascript:void(0)"
                        class="text-primary me-2"
                        @click.prevent="downloadPdf(trip)"
                        ><i class="ri-download-line fs-18 lh-1"></i>PDF</a
                      >
                    </td>
                    <td class="text-center" v-if="trip?.status_flag == 1">
                      <a
                        target="_blank"
                        :href="
                          $store.getters.apiUrl +
                          'downloadReportWordDoc?trip_id=' +
                          trip.trip_id
                        "
                        class="text-primary me-2"
                        ><i class="ri-download-line fs-18 lh-1"></i>Word</a
                      >
                    </td>
                    <td class="text-center" v-if="trip?.status_flag == 1">
                      <input
                        type="checkbox"
                        :value="trip.trip_id"
                        v-model="selectedTripIds"
                        @change="$nextTick(updateHeaderCheckboxVisual)"
                      />
                    </td>
                    <td v-if="trip?.status_flag != 1"></td>
                    <td v-if="trip?.status_flag != 1"></td>
                    <td v-if="trip?.status_flag != 1"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="card-footer">
            <div class="d-flex justify-content-between align-items-center">
              <select
                class="form-select from-select-sm width-75"
                v-model="meta.per_page"
                @change="onPerPageChange"
              >
                <option>10</option>
                <option>15</option>
                <option>20</option>
                <option>25</option>
                <option>30</option>
              </select>
              <span
                >Showing {{ meta.from }} to {{ meta.to }} of
                {{ meta.totalRows }} entries</span
              >
              <Pagination
                :maxPage="meta.maxPage"
                :totalPages="meta.lastPage"
                :currentPage="meta.page"
                @pagechanged="onPageChange"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      class="modal fade"
      tabindex="-1"
      id="tripFilterModal"
      ref="tripFilterModal"
    >
      <div class="modal-dialog modal-lg modal-dialog-scrollable">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">Filter Trips</h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div class="row g-3">
              <div class="col-sm-6 col-md-4">
                <label>Customer</label>
                <select class="form-select" v-model="meta.customer_id">
                  <option value="">All</option>
                  <option v-for="c in customers" :key="c.id" :value="c.id">
                    {{ c.name }}
                  </option>
                </select>
              </div>
              <div class="col-sm-6 col-md-4">
                <label>Start Date Time</label>
                <input
                  type="datetime-local"
                  class="form-control"
                  v-model="meta.start_date_time"
                />
              </div>
              <div class="col-sm-6 col-md-4">
                <label>End Date Time</label>
                <input
                  type="datetime-local"
                  class="form-control"
                  v-model="meta.end_date_time"
                />
              </div>
              <div class="col-sm-6 col-md-4">
                <label>Grade</label>
                <select class="form-select" v-model="meta.grade">
                  <option value="">All</option>
                  <option v-for="g in grades" :key="g" :value="g">
                    {{ g }}
                  </option>
                </select>
              </div>
              <div class="col-sm-6 col-md-4">
                <label>Packing</label>
                <select class="form-select" v-model="meta.packing">
                  <option value="">All</option>
                  <option v-for="p in packings" :key="p" :value="p">
                    {{ p }}
                  </option>
                </select>
              </div>
              <div class="col-sm-6 col-md-4">
                <label>Loaded Bags</label>
                <select class="form-select" v-model="meta.loaded_bags">
                  <option value="">All</option>
                  <option v-for="b in loadedBags" :key="b" :value="b">
                    {{ b }}
                  </option>
                </select>
              </div>
              <div class="col-12">
                <label>Search</label>
                <input
                  class="form-control"
                  type="text"
                  placeholder="Type keyword and press enter key"
                  v-model="meta.search"
                  @keypress.enter="applyFilters"
                />
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-outline-secondary"
              @click="clearFilters"
            >
              Clear
            </button>
            <button
              type="button"
              class="btn btn-secondary"
              data-bs-dismiss="modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-primary"
              @click="applyFilters"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
<script>
import Pagination from "@/components/Pagination.vue";
import moment from "moment";
export default {
  components: {
    Pagination,
  },
  data() {
    return {
      trip_status: false,
      meta: {
        search: "",
        order_by: "desc",
        keyword: "trip_id",
        per_page: 10,
        totalRows: 0,
        page: 1,
        lastPage: 1,
        from: 1,
        to: 1,
        maxPage: 1,
        trashed: false,
        status: "",
        customer_id: "",
        start_date_time: "",
        end_date_time: "",
        grade: "",
        packing: "",
        loaded_bags: "",
      },
      from_name: "",

      trips: [],
      errors: [],
      // update: false,
      status: true,
      customers: [],
      grades: [],
      packings: [],
      loadedBags: [],
      // background export state
      bgExporting: false,
      exportId: null,
      pollTimer: null,
      downloadingZip: false,
      selectedTripIds: [],
      eligibleTripIdsAllPages: [],
      loadingSelectAll: false,
    };
  },
  beforeRouteEnter(to, from, next) {
    next((vm) => {
      vm.from_name = from.name;
    });
  },
  mounted() {
    if (!this.from_name || this.from_name == "Dashboard") {
      this.meta.status = this.$store.getters.status;
    }
    this.fetchFilters();
    this.index();
  },

  computed: {
    isAllSelectedOnPage() {
      // Reflect global "select all" if we have full eligible list and all are selected
      if (this.eligibleTripIdsAllPages.length > 0) {
        return (
          this.selectedTripIds.length > 0 &&
          this.selectedTripIds.length === this.eligibleTripIdsAllPages.length
        );
      }
      // Fallback: only consider current page if global list not loaded
      const eligible = this.trips
        .filter((t) => t?.status_flag == 1)
        .map((t) => t.trip_id);
      if (eligible.length === 0) {
        return false;
      }
      return eligible.every((id) => this.selectedTripIds.includes(id));
    },
    hasActiveFilters() {
      return this.activeFilterCount > 0;
    },
    activeFilterCount() {
      const fields = [
        this.meta.customer_id,
        this.meta.start_date_time,
        this.meta.end_date_time,
        this.meta.grade,
        this.meta.packing,
        this.meta.loaded_bags,
        this.meta.search,
      ];
      return fields.filter((value) => value !== "" && value != null).length;
    },
    isStatusInProgress() {
      return (
        this.bgExporting ||
        this.isInProgressStatus(this.$store.getters.download_status)
      );
    },
    statusLabel() {
      if (this.bgExporting) {
        return "Processing";
      }
      return this.$store.getters.download_status || "";
    },
    statusBadgeClass() {
      const status = (this.$store.getters.download_status || "")
        .toString()
        .toLowerCase();
      if (this.isStatusInProgress) {
        return "bg-warning text-dark";
      }
      if (status.includes("fail") || status.includes("error")) {
        return "bg-danger";
      }
      if (
        status.includes("complete") ||
        status.includes("ready") ||
        status.includes("success")
      ) {
        return "bg-success";
      }
      return "bg-secondary";
    },
  },

  methods: {
    sanitizeFilenamePart(value) {
      const v = (value ?? "").toString().trim();
      if (!v) return "NA";
      // Keep letters/numbers/_- and spaces, then collapse spaces to single hyphen
      const cleaned = v.replace(/[^a-zA-Z0-9 _-]+/g, " ").trim();
      return cleaned.replace(/\s+/g, "-") || "NA";
    },
    buildTripPdfFilename(trip) {
      const container = this.sanitizeFilenamePart(trip?.container_no);
      const grade = this.sanitizeFilenamePart(trip?.details?.grade);
      const lot = this.sanitizeFilenamePart(trip?.details?.lot_nos);
      const date = trip?.trip_date_time
        ? moment(trip.trip_date_time).format("YYYYMMDD")
        : moment().format("YYYYMMDD");
      return `${container}-${grade}-${lot}-${date}.pdf`;
    },
    async downloadPdf(trip) {
      let loader = null;
      try {
        loader = this.$loading?.show ? this.$loading.show() : null;
        const axios = (await import("axios")).default;
        const url = this.$store.getters.apiUrl + "generatePDFReport?trip_id=" + trip.trip_id;
        const resp = await axios.get(url, {
          responseType: "blob",
          headers: { Authorization: "Bearer" + " " + this.$store.getters.token },
        });
        const blob = new Blob([resp.data], { type: "application/pdf" });
        const dlUrl = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = dlUrl;
        a.download = this.buildTripPdfFilename(trip);
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(dlUrl);
      } catch (err) {
        const msg = err?.response?.data?.message || "Failed to download PDF";
        this.$store.dispatch("error", msg);
      } finally {
        if (loader && typeof loader.hide === "function") {
          loader.hide();
        }
      }
    },
    isInProgressStatus(status) {
      const s = (status || "").toString().toLowerCase();
      return [
        "pending",
        "processing",
        "queued",
        "running",
        "in progress",
        "in_progress",
      ].includes(s);
    },
    fetchFilters() {
      let vm = this;
      vm.$store
        .dispatch("post", { uri: "tripFilters", data: vm.meta })
        .then((response) => {
          vm.customers = response.data.customers || [];
          vm.grades = response.data.grades || [];
          vm.packings = response.data.packings || [];
          vm.loadedBags = response.data.loaded_bags || [];
        })
        .catch(function (error) {
          vm.errors = error.response?.data?.errors || {};
        });
    },
    downloadWordFormat(trip) {
      window.open(
        this.$store.getters.apiUrl +
          "downloadReportWordDoc?trip_id=" +
          trip.trip_id,
      );
    },
    convertDateFormat(trip, date_time) {
      if (trip.status_flag == 1) {
        this.trip_status = true;
      }
      if (date_time) {
        return moment(date_time).format("DD-MM-yyyy hh:mm a");
      } else {
        return null;
      }
    },
    search() {
      let vm = this;
      vm.meta.page = 1;
      vm.fetchFilters();
      vm.index();
    },
    openFilterModal() {
      this.fetchFilters();
      const el = this.$refs.tripFilterModal;
      if (el && window.bootstrap?.Modal) {
        window.bootstrap.Modal.getOrCreateInstance(el).show();
      }
    },
    closeFilterModal() {
      const el = this.$refs.tripFilterModal;
      if (el && window.bootstrap?.Modal) {
        const instance = window.bootstrap.Modal.getInstance(el);
        if (instance) {
          instance.hide();
        }
      }
    },
    applyFilters() {
      this.search();
      this.closeFilterModal();
    },
    clearFilters() {
      this.meta.customer_id = "";
      this.meta.start_date_time = "";
      this.meta.end_date_time = "";
      this.meta.grade = "";
      this.meta.packing = "";
      this.meta.loaded_bags = "";
      this.meta.search = "";
      this.search();
    },
    index() {
      let vm = this;
      if (this.$store.getters.page) {
        this.meta.page = this.$store.getters.page;
      }
      vm.$store
        .dispatch("post", { uri: "paginateTrips", data: vm.meta })
        .then((response) => {
          vm.trips = response.data.data;
          vm.meta.totalRows = response.data.meta.total;
          vm.meta.lastPage = response.data.meta.last_page;
          vm.meta.from = response.data.meta.from;
          vm.meta.to = response.data.meta.to;
          this.$store.dispatch("setPage", null);
          //vm.meta.lastPage = response.data.meta.last_page;
          vm.meta.maxPage = vm.meta.lastPage >= 3 ? 3 : vm.meta.lastPage;
        })
        .catch(function (error) {
          vm.errors = error.response.data.errors;
          vm.$store.dispatch("error", error.response.data.message);
        });
    },
    edit(trip) {
      this.$store.dispatch("setPage", this.meta.page);
      this.$router.push("/trip/" + trip.trip_id + "/edit");
    },
    view(trip) {
      this.$router.push("/trip/" + trip.trip_id + "/view");
    },
    deleteTrip(trip) {
      let vm = this;
      let loader = vm.$loading.show();
      vm.$store
        .dispatch("post", { uri: "deleteTrip", data: trip })
        .then((response) => {
          loader.hide();
          vm.$store.dispatch("success", response.data.message);
          vm.index();
        })
        .catch(function (error) {
          loader.hide();
          vm.errors = error.response.data.errors;
          vm.$store.dispatch("error", error.response.data.message);
        });
    },
    onPageChange(page) {
      let vm = this;
      vm.meta.page = page;
      vm.index();
    },
    sort(field) {
      let vm = this;
      vm.meta.keyword = field;
      vm.meta.order_by = vm.meta.order_by == "asc" ? "desc" : "asc";
      vm.index();
    },
    onPerPageChange() {
      this.meta.page = 1;
      this.index();
    },
    async startBackgroundExportSelected() {
      try {
        if (this.selectedTripIds.length === 0) {
          return;
        }
        this.bgExporting = true;
        const payload = { trip_ids: [...this.selectedTripIds] };
        const res = await this.$store.dispatch("post", {
          uri: "exports/trips-zip",
          data: payload,
        });
        this.exportId = res.data.export_id;
        this.$store.dispatch(
          "info",
          "We are preparing your ZIP for selected trips.",
        );
        if (this.pollTimer) {
          clearInterval(this.pollTimer);
        }
        this.pollTimer = setInterval(this.checkExportStatus, 8000);
      } catch (err) {
        const msg = err?.response?.data?.message || "Failed to start export";
        this.$store.dispatch("error", msg);
        this.bgExporting = false;
      }
    },
    toggleSelectAllOnPage(e) {
      const checked = !!e.target.checked;
      this.toggleSelectAllGlobal(checked);
      this.$nextTick(this.updateHeaderCheckboxVisual);
    },
    async toggleSelectAllGlobal(checked) {
      if (checked) {
        try {
          this.loadingSelectAll = true;
          const axios = (await import("axios")).default;
          const res = await axios.post(
            this.$store.getters.apiUrl + "listEligibleTripIds",
            this.meta,
            {
              headers: {
                Authorization: "Bearer" + " " + this.$store.getters.token,
              },
            },
          );
          this.eligibleTripIdsAllPages = res.data?.trip_ids || [];
          this.selectedTripIds = [...this.eligibleTripIdsAllPages];
        } catch (err) {
          const msg =
            err?.response?.data?.message || "Failed to load eligible trips";
          this.$store.dispatch("error", msg);
        } finally {
          this.loadingSelectAll = false;
        }
      } else {
        this.selectedTripIds = [];
      }
      this.$nextTick(this.updateHeaderCheckboxVisual);
    },
    updateHeaderCheckboxVisual() {
      const el = this.$refs.selectAllCheckbox;
      if (!el) return;
      const total = this.eligibleTripIdsAllPages.length;
      const sel = this.selectedTripIds.length;
      if (total > 0) {
        el.indeterminate = sel > 0 && sel < total;
        el.checked = sel === total && total > 0;
      } else {
        const pageIds = this.trips
          .filter((t) => t?.status_flag == 1)
          .map((t) => t.trip_id);
        const pageTotal = pageIds.length;
        const pageSel = pageIds.filter((id) =>
          this.selectedTripIds.includes(id),
        ).length;
        el.indeterminate = pageSel > 0 && pageSel < pageTotal;
        el.checked = pageTotal > 0 && pageSel === pageTotal;
      }
    },
    async checkExportStatus() {
      let vm = this;
      if (!vm.exportId) {
        return;
      }
      try {
        const res = await vm.$store.dispatch("get", {
          uri: `exports/${vm.exportId}`,
        });
        const { status, id } = res.data;
        vm.$store.dispatch("setDownloadStatus", status);
        if (status === "completed") {
          vm.$store.dispatch("setDownloadStatus", false);
          clearInterval(vm.pollTimer);
          vm.pollTimer = null;
          vm.bgExporting = false;
          // Download with auth header (can't set headers via window.open)
          const axios = (await import("axios")).default;
          const url = vm.$store.getters.apiUrl + `exports/${id}/download`;
          const resp = await axios.get(url, {
            responseType: "blob",
            headers: {
              Authorization: "Bearer" + " " + vm.$store.getters.token,
            },
          });
          const disposition =
            resp.headers?.["content-disposition"] ||
            resp.headers?.["Content-Disposition"];
          let filename = `Filtered-Trips-${new Date()
            .toISOString()
            .slice(0, 19)
            .replace(/[:T]/g, "-")}.zip`;
          if (disposition) {
            const match =
              /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(
                disposition,
              );
            const raw = decodeURIComponent(match?.[1] || match?.[2] || "");
            if (raw) {
              filename = raw;
            }
          }
          const blob = new Blob([resp.data], { type: "application/zip" });
          const dlUrl = window.URL.createObjectURL(blob);
          const a = document.createElement("a");
          a.href = dlUrl;
          a.download = filename;
          document.body.appendChild(a);
          a.click();
          a.remove();
          window.URL.revokeObjectURL(dlUrl);
          vm.$store.dispatch(
            "success",
            "Download process completed successfully",
          );
          // Clear all selections after successful download
          this.selectedTripIds = [];
          this.eligibleTripIdsAllPages = [];
          this.$nextTick(this.updateHeaderCheckboxVisual);
        } else if (status === "failed") {
          clearInterval(vm.pollTimer);
          vm.pollTimer = null;
          vm.bgExporting = false;
          const errMsg = res.data.error || "Export failed";
          vm.$store.dispatch("error", errMsg);
        }
      } catch (err) {
        if (err?.response?.status === 404) {
          clearInterval(vm.pollTimer);
          vm.pollTimer = null;
          vm.bgExporting = false;
          vm.$store.dispatch("error", "Export not found.");
        }
      }
    },
    async downloadFilteredZip() {
      try {
        this.downloadingZip = true;
        const res =
          (await this.$axios?.post(
            this.$store.getters.apiUrl + "downloadFilteredTripsZip",
            this.meta,
            {
              responseType: "blob",
              headers: {
                Authorization: "Bearer" + " " + this.$store.getters.token,
              },
            },
          )) ||
          (await (async () => {
            // fallback via store action
            const axios = (await import("axios")).default;
            return axios.post(
              this.$store.getters.apiUrl + "downloadFilteredTripsZip",
              this.meta,
              {
                responseType: "blob",
                headers: {
                  Authorization: "Bearer" + " " + this.$store.getters.token,
                },
              },
            );
          })());
        const disposition =
          res.headers?.["content-disposition"] ||
          res.headers?.["Content-Disposition"];
        let filename = `Filtered-Trips-${new Date()
          .toISOString()
          .slice(0, 19)
          .replace(/[:T]/g, "-")}.zip`;
        if (disposition) {
          const match = /filename\*=UTF-8''([^;]+)|filename="?([^"]+)"?/i.exec(
            disposition,
          );
          const raw = decodeURIComponent(match?.[1] || match?.[2] || "");
          if (raw) {
            filename = raw;
          }
        }
        const blob = new Blob([res.data], { type: "application/zip" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        const msg = err?.response?.data?.message || "Failed to prepare ZIP";
        this.$store.dispatch("error", msg);
      } finally {
        this.downloadingZip = false;
        // Clear any prior selections after any download completes
        this.selectedTripIds = [];
        this.eligibleTripIdsAllPages = [];
        this.$nextTick(this.updateHeaderCheckboxVisual);
      }
    },
  },
};
</script>

<style scoped>
.ellipsis::after {
  content: "";
  display: inline-block;
  width: 1.2em;
  text-align: left;
  animation: dots 1.2s steps(4, end) infinite;
}
@keyframes dots {
  0% {
    content: "";
  }
  25% {
    content: ".";
  }
  50% {
    content: "..";
  }
  75% {
    content: "...";
  }
  100% {
    content: "";
  }
}
</style>
